import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import globalSettingsBranches from '../../../../../stores/global-settings-branches';
import globalSettingsStores from '../../../../../stores/settings-stores';
import { getHebrewYear } from '../../../../../utils/hebrew-date-utils';
import { FilterExpanderWithListOfCheckboxes } from '../../../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import { CustomExpander } from '../../../../../components/CustomExpander/CustomExpander';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { ProgramFinancialSideBarFiltersDatamodelType } from './program-financial-side-bar-filters-datamodel.type';
import { toast } from 'react-toastify';

interface YearOption {
  year: string;
  code: string;
  value: number;
  id: number;
}

interface StudyYearOption {
  label: string;
  value: number;
  id: number;
}

export interface ProgramFinancialSideBarFilterDatamodel {
  onSearch: (filters: ProgramFinancialSideBarFiltersDatamodelType) => any;
}

export const ProgramFinancialSideBarFilter: React.FC<ProgramFinancialSideBarFilterDatamodel> =
  observer(({ onSearch }) => {
    const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
    const [selectedPrograms, setSelectedPrograms] = useState<number[]>([]);
    const [selectedStartYears, setSelectedStartYears] = useState<number[]>([]);
    const [selectedStudyYears, setSelectedStudyYears] = useState<number[]>([]);

    // Generate years array
    const years: YearOption[] = [];
    for (let year = 1980; year <= 2030; year++) {
      years.push({
        year: `${getHebrewYear(year)} (${year} - ${year + 1})`,
        code: 'year',
        value: year,
        id: year,
      });
    }

    const studyYears: StudyYearOption[] = [
      { label: 'שנה א׳', value: 1, id: 0 },
      { label: 'שנה ב׳', value: 2, id: 1 },
      { label: 'שנה ג׳', value: 3, id: 2 },
    ];

    const handleBranchToggle = (branchId: number) => {
      setSelectedBranches((prev) =>
        prev.includes(branchId)
          ? prev.filter((b) => b !== branchId)
          : [...prev, branchId],
      );
    };

    const handleProgramToggle = (programId: number) => {
      setSelectedPrograms((prev) =>
        prev.includes(programId)
          ? prev.filter((p) => p !== programId)
          : [...prev, programId],
      );
    };

    const handleStartYearToggle = (year: number) => {
      setSelectedStartYears((prev) =>
        prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year],
      );
    };

    const handleStudyYearToggle = (year: number) => {
      setSelectedStudyYears((prev) =>
        prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year],
      );
    };

    const handleSearch = () => {
      if (selectedPrograms.length === 0) {
        toast.info('יש לבחור לפחות תוכנית לימודים אחת');
        return;
      }

      const filters: ProgramFinancialSideBarFiltersDatamodelType = {
        branchIds: selectedBranches,
        abstractProgramIds: selectedPrograms,
        startYears: selectedStartYears,
        studyYears: selectedStudyYears,
      };
      onSearch(filters);
    };

    const handleClearAllFilters = () => {
      setSelectedBranches([]);
      setSelectedPrograms([]);
      setSelectedStartYears([]);
      setSelectedStudyYears([]);
    };

    const styles = {
      container: {
        height: '100%',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      header: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: '#333',
      },
      headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        gap: '8px',
      },
      content: {
        flex: 1,
        overflowY: 'auto' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '16px',
        paddingRight: '4px',
      },
      dropdownContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
      },
      dropdownLabel: {
        fontSize: '0.9rem',
        color: '#666',
      },
      buttonContainer: {
        marginTop: '16px',
        padding: '8px',
        borderTop: '1px solid #e9ecef',
      },
      searchButton: {
        width: '100%',
        backgroundColor: '#2196F3',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
      },
    };

    return (
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div style={styles.header}>סינונים</div>
          <Button
            icon="pi pi-filter-slash"
            text
            severity="secondary"
            tooltip={'נקה הכל'}
            onClick={handleClearAllFilters}
          />
        </div>
        <div style={styles.content}>
          <FilterExpanderWithListOfCheckboxes
            title="שלוחות"
            items={globalSettingsBranches.branches}
            selectedItems={selectedBranches}
            onItemToggle={handleBranchToggle}
            getItemLabel={(branch) => branch.branchName}
            getItemKey={(branch) => branch.id}
            defaultExpanded={true}
            searchPlaceholder="חפש שלוחה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="תוכניות לימודים"
            items={globalSettingsStores.abstractProgram}
            selectedItems={selectedPrograms}
            onItemToggle={handleProgramToggle}
            getItemLabel={(program) => program.programName}
            getItemKey={(program) => program.id}
            defaultExpanded={true}
            searchPlaceholder="חפש תוכנית לימודים..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="שנת התחלה"
            items={years}
            selectedItems={selectedStartYears}
            onItemToggle={handleStartYearToggle}
            getItemLabel={(year) => year.year}
            getItemKey={(year) => year.id}
            defaultExpanded={false}
            searchPlaceholder="חפש שנה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="שנת לימוד"
            items={studyYears}
            selectedItems={selectedStudyYears}
            onItemToggle={handleStudyYearToggle}
            getItemLabel={(studyYear) => studyYear.label}
            getItemKey={(studyYear) => studyYear.id}
            defaultExpanded={false}
            searchPlaceholder="חפש שנת לימוד..."
          />
        </div>

        <div style={styles.buttonContainer}>
          <Button
            label="חפש"
            icon="pi pi-search"
            style={styles.searchButton}
            onClick={handleSearch}
          />
        </div>
      </div>
    );
  });
